import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Youtube, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { data } from '@/data';
import { useTranslation } from '@/hooks/useTranslation';
import { useLanguageContext } from '@/context/LanguageContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef(null);
  const languageMenuRef = useRef(null);
  const t = useTranslation();
  const { language, switchLanguage } = useLanguageContext();

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setLanguageMenuOpen(false);
      }
    };

    if (mobileMenuOpen || languageMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen, languageMenuOpen]);

  const navLinks = [
    { name: t.navbar.home, path: '/' },
    { name: t.navbar.about, path: '/about' },
    { name: t.navbar.media, path: '/media' },
    { name: t.navbar.gallery, path: '/gallery' },
    { name: t.navbar.repertoire, path: '/repertoire' },
    { name: t.navbar.contact, path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  const handleLanguageChange = (newLanguage) => {
    switchLanguage(newLanguage);
    setLanguageMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-stone-200/50 shadow-sm" ref={mobileMenuRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo/Name */}
          <Link to="/" className="text-2xl font-playfair tracking-tight text-gray-800 hover:text-gray-600 transition-colors font-semibold">
            {data.musician.name}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm tracking-wide transition-colors ${
                  isActive(link.path)
                    ? 'text-gray-900 font-medium'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Language Switcher */}
            <div className="relative" ref={languageMenuRef}>
              <button
                onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                className="flex items-center space-x-1 text-sm tracking-wide text-gray-600 hover:text-gray-900 transition-colors"
                aria-label={t.aria.languageSwitcher}
              >
                <span>{language.toUpperCase()}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${languageMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {languageMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[80px] z-50"
                  >
                    <button
                      onClick={() => handleLanguageChange('en')}
                      className={`block w-full px-4 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                        language === 'en' ? 'text-gray-900 font-medium' : 'text-gray-600'
                      }`}
                    >
                      {t.language.english}
                    </button>
                    <button
                      onClick={() => handleLanguageChange('pt')}
                      className={`block w-full px-4 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                        language === 'pt' ? 'text-gray-900 font-medium' : 'text-gray-600'
                      }`}
                    >
                      {t.language.portuguese}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            <a
              href={data.musician.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-red-600 transition-colors"
              aria-label={t.navbar.youtube}
            >
              <Youtube className="w-5 h-5" />
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-gray-900 hover:text-gray-700 transition-colors"
            aria-label={t.aria.toggleMenu}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white border-t border-gray-100"
          >
            <nav className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block text-base transition-colors ${
                    isActive(link.path)
                      ? 'text-gray-900 font-medium'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile Language Switcher */}
              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500 mb-2">Language / Idioma</p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      handleLanguageChange('en');
                      setMobileMenuOpen(false);
                    }}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      language === 'en' 
                        ? 'bg-gray-900 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {t.language.english}
                  </button>
                  <button
                    onClick={() => {
                      handleLanguageChange('pt');
                      setMobileMenuOpen(false);
                    }}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      language === 'pt' 
                        ? 'bg-gray-900 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {t.language.portuguese}
                  </button>
                </div>
              </div>
              
              <a
                href={data.musician.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors pt-2"
              >
                <Youtube className="w-5 h-5" />
                <span className="text-base">{t.navbar.youtube}</span>
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;